import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { PremiumOverlay } from '../../components/ui/premium-overlay';
import { PremiumBadge } from '../../components/ui/premium-badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Spinner } from '../../components/ui/spinner';
import { formatCurrency } from '../../lib/i18n';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  Area,
  AreaChart
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Package,
  Wrench,
  Users,
  DollarSign,
  AlertTriangle,
  BarChart3,
  ArrowRight,
  Cog,
  PiggyBank,
  Banknote,
  Save,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Home,
  Zap,
  Droplets,
  Receipt,
  Megaphone,
  MoreHorizontal,
  Plus,
  X,
  Settings2,
  Eye,
  EyeOff,
  Tag,
  Send,
  Gift,
  Heart,
  UserPlus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#1e40af', '#f97316', '#16a34a', '#eab308', '#dc2626', '#8b5cf6'];

const EXPENSE_ICON_MAP = {
  salary: Users,
  rent: Home,
  electricity: Zap,
  water: Droplets,
  tax: Receipt,
  tools: Wrench,
  marketing: Megaphone,
  misc: MoreHorizontal,
};

const DEFAULT_EXPENSE_CATEGORIES = [
  { key: 'salary',      en: 'Employee Salaries',  bn: 'কর্মচারী বেতন' },
  { key: 'rent',        en: 'Rent',               bn: 'ভাড়া' },
  { key: 'electricity', en: 'Electricity',        bn: 'বিদ্যুৎ' },
  { key: 'water',       en: 'Water / Utilities',  bn: 'পানি / ইউটিলিটি' },
  { key: 'tax',         en: 'Tax',                bn: 'কর' },
  { key: 'tools',       en: 'Tools & Equipment',  bn: 'যন্ত্রপাতি' },
  { key: 'marketing',   en: 'Marketing',          bn: 'বিপণন' },
  { key: 'misc',        en: 'Miscellaneous',      bn: 'বিবিধ' },
];

const MONTH_NAMES_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTH_NAMES_BN = ['জানুয়ারি','ফেব্রুয়ারি','মার্চ','এপ্রিল','মে','জুন','জুলাই','আগস্ট','সেপ্টেম্বর','অক্টোবর','নভেম্বর','ডিসেম্বর'];

const formatMonthName = (yyyyMM, lang) => {
  const [y, m] = yyyyMM.split('-');
  const mi = parseInt(m, 10) - 1;
  const name = lang === 'en' ? MONTH_NAMES_EN[mi] : MONTH_NAMES_BN[mi];
  return `${name} ${y}`;
};

const formatMonthShort = (yyyyMM, lang) => {
  const [, m] = yyyyMM.split('-');
  const mi = parseInt(m, 10) - 1;
  return lang === 'en' ? MONTH_NAMES_EN[mi]?.slice(0, 3) : MONTH_NAMES_BN[mi]?.slice(0, 3);
};

// ==================== Category Label Map ====================
const CATEGORY_LABELS = {
  engine:       { en: 'Engine',        bn: 'ইঞ্জিন' },
  brake:        { en: 'Brake',         bn: 'ব্রেক' },
  electrical:   { en: 'Electrical',    bn: 'ইলেকট্রিক্যাল' },
  transmission: { en: 'Transmission',  bn: 'ট্রান্সমিশন' },
  accessories:  { en: 'Accessories',   bn: 'এক্সেসরিজ' },
  lubricants:   { en: 'Lubricants',    bn: 'লুব্রিকেন্ট' },
  filters:      { en: 'Filters',       bn: 'ফিল্টার' },
  bearings:     { en: 'Bearings',      bn: 'বিয়ারিং' },
  suspension:   { en: 'Suspension',    bn: 'সাসপেনশন' },
  clutch:       { en: 'Clutch',        bn: 'ক্লাচ' },
  tires:        { en: 'Tires & Tubes', bn: 'টায়ার ও টিউব' },
  cables:       { en: 'Cables',        bn: 'ক্যাবল' },
  body_parts:   { en: 'Body Parts',    bn: 'বডি পার্টস' },
};

export default function ReportsPage() {
  const { t, language } = useLanguage();
  const { isPremium } = useAuth();
  const navigate = useNavigate();
  const { garageSlug } = useParams();
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState('month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [revenueData, setRevenueData] = useState(null);
  const [jobsData, setJobsData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [todayDetail, setTodayDetail] = useState(null);
  const [dailyChart, setDailyChart] = useState(null);
  // Profits tab state
  const [profitsData, setProfitsData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [expenseInputs, setExpenseInputs] = useState({});
  const [expenseSaving, setExpenseSaving] = useState({});
  const [expenseMonth, setExpenseMonth] = useState(new Date().toISOString().slice(0, 7));
  // Category settings
  const [catSettings, setCatSettings] = useState({ disabled_defaults: [], custom_categories: [] });
  const [showCatManager, setShowCatManager] = useState(false);
  const [newCatEn, setNewCatEn] = useState('');
  const [newCatBn, setNewCatBn] = useState('');
  const [activeTab, setActiveTab] = useState('revenue');
  const [paymentsData, setPaymentsData] = useState(null);
  const [marketingData, setMarketingData] = useState(null);
  const [categorySortMode, setCategorySortMode] = useState('value-desc');

  // Switch period default when tab changes
  useEffect(() => {
    if (activeTab === 'payments') {
      setPeriod('day');
    } else if (period === 'day') {
      setPeriod('month');
    }
  }, [activeTab]);

  // Reset payments data when period changes to avoid stale display
  useEffect(() => {
    setPaymentsData(null);
  }, [period]);

  const currentYear = new Date().getFullYear();
  const currentMonthNum = new Date().getMonth() + 1;
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const monthOptions = selectedYear === currentYear
    ? Array.from({ length: currentMonthNum }, (_, i) => i + 1)
    : Array.from({ length: 12 }, (_, i) => i + 1);

  // Build effective expense categories
  const activeCategories = [
    ...DEFAULT_EXPENSE_CATEGORIES.filter(c => !catSettings.disabled_defaults.includes(c.key)),
    ...catSettings.custom_categories.map(c => ({ key: c.key, en: c.name_en, bn: c.name_bn, isCustom: true })),
  ];

  useEffect(() => {
    fetchReports();
  }, [period, selectedYear, selectedMonth, activeTab]);

  // Sync expenseMonth with selectedMonth/selectedYear when period is 'month'
  useEffect(() => {
    if (period === 'month') {
      setExpenseMonth(`${selectedYear}-${String(selectedMonth).padStart(2, '0')}`);
    }
  }, [period, selectedYear, selectedMonth]);

  // Clamp selectedMonth when switching to a year that has fewer available months
  useEffect(() => {
    if (selectedYear === currentYear && selectedMonth > currentMonthNum) {
      setSelectedMonth(currentMonthNum);
    }
  }, [selectedYear]);

  // Re-fetch expenses when expenseMonth changes
  useEffect(() => {
    fetchExpensesForMonth(expenseMonth);
  }, [expenseMonth]);

  const fetchExpensesForMonth = async (month) => {
    try {
      const res = await axios.get(`${API}/expenses?period_month=${month}`);
      const expMap = {};
      (res.data.expenses || []).forEach(e => { expMap[e.category] = e.amount; });
      setExpenses(res.data.expenses || []);
      setExpenseInputs(expMap);
    } catch (err) {
      console.error('Failed to fetch expenses:', err);
    }
  };

  const fetchReports = async () => {
    if (revenueData) setRefreshing(true); else setInitialLoading(true);
    const yearParam = period === 'year' ? `&year=${selectedYear}` :
                      period === 'month' ? `&year=${selectedYear}&month=${selectedMonth}` : '';
    const effectiveExpenseMonth = period === 'month'
      ? `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`
      : expenseMonth;
    try {
      // Always fetch core data (today detail + daily chart are lightweight)
      const coreCalls = [
        axios.get(`${API}/reports/today-detail`),
        axios.get(`${API}/reports/daily-chart?period=${period}${yearParam}`),
        axios.get(`${API}/expense-category-settings`),
      ];

      // Fetch tab-specific data based on active tab
      const tabCalls = [];
      if (activeTab === 'revenue' || !revenueData) {
        tabCalls.push(axios.get(`${API}/reports/revenue?period=${period}${yearParam}`).then(r => setRevenueData(r.data)));
      }
      if (activeTab === 'inventory' || !inventoryData) {
        tabCalls.push(axios.get(`${API}/reports/inventory`).then(r => setInventoryData(r.data)));
      }
      if (activeTab === 'jobs' || !jobsData) {
        tabCalls.push(axios.get(`${API}/reports/jobs?period=${period}${yearParam}`).then(r => setJobsData(r.data)).catch(() => {}));
      }
      if (activeTab === 'payments') {
        tabCalls.push(axios.get(`${API}/reports/payments?period=${period}${yearParam}`).then(r => setPaymentsData(r.data)).catch(() => {}));
      }
      if (activeTab === 'profits') {
        tabCalls.push(
          axios.get(`${API}/reports/profits?period=${period}${yearParam}`).then(r => setProfitsData(r.data)).catch(() => {}),
          axios.get(`${API}/expenses?period_month=${effectiveExpenseMonth}`).then(r => {
            const expMap = {};
            (r.data.expenses || []).forEach(e => { expMap[e.category] = e.amount; });
            setExpenses(r.data.expenses || []);
            setExpenseInputs(expMap);
          }).catch(() => {})
        );
      }
      if (activeTab === 'marketing-impact') {
        tabCalls.push(axios.get(`${API}/reports/marketing-impact?period=${period}${yearParam}`).then(r => setMarketingData(r.data)).catch(() => {}));
      }

      const [todayRes, chartRes, catSettingsRes] = await Promise.all(coreCalls);
      setTodayDetail(todayRes.data);
      setDailyChart(chartRes.data);
      setCatSettings(catSettingsRes.data);

      // Wait for tab-specific calls
      await Promise.all(tabCalls);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  };

  const periods = [
    { value: 'week', label: language === 'en' ? 'This Week' : 'এই সপ্তাহ' },
    { value: 'month', label: language === 'en' ? 'This Month' : 'এই মাস' },
    { value: 'year', label: language === 'en' ? 'This Year' : 'এই বছর' },
  ];

  const paymentPeriods = [
    { value: 'day', label: language === 'en' ? 'Today' : 'আজ' },
    { value: 'week', label: language === 'en' ? 'This Week' : 'এই সপ্তাহ' },
    { value: 'month', label: language === 'en' ? 'This Month' : 'এই মাস' },
    { value: 'year', label: language === 'en' ? 'This Year' : 'এই বছর' },
  ];

  const periodLabel = { day: language === 'en' ? 'Today' : 'আজ', week: language === 'en' ? 'This Week' : 'এই সপ্তাহ', month: language === 'en' ? 'This Month' : 'এই মাস', year: language === 'en' ? 'This Year' : 'এই বছর' }[period] || '';

  const saveExpense = async (category) => {
    const amount = parseFloat(expenseInputs[category] || 0);
    if (isNaN(amount) || amount < 0) return;
    setExpenseSaving(prev => ({ ...prev, [category]: true }));
    const yearParam = period === 'year' ? `&year=${selectedYear}` :
                      period === 'month' ? `&year=${selectedYear}&month=${selectedMonth}` : '';
    try {
      await axios.post(`${API}/expenses`, {
        period_month: expenseMonth,
        category,
        amount,
        note: '',
      });
      const [profitsRes, expensesRes] = await Promise.all([
        axios.get(`${API}/reports/profits?period=${period}${yearParam}`),
        axios.get(`${API}/expenses?period_month=${expenseMonth}`),
      ]);
      setProfitsData(profitsRes.data);
      const expMap = {};
      (expensesRes.data.expenses || []).forEach(e => { expMap[e.category] = e.amount; });
      setExpenses(expensesRes.data.expenses || []);
      setExpenseInputs(expMap);
    } catch (err) {
      console.error('Failed to save expense:', err);
    } finally {
      setExpenseSaving(prev => ({ ...prev, [category]: false }));
    }
  };

  const saveCatSettings = async (newSettings) => {
    try {
      const res = await axios.put(`${API}/expense-category-settings`, newSettings);
      setCatSettings(res.data);
    } catch (err) {
      console.error('Failed to save category settings:', err);
    }
  };

  const toggleDefault = (key) => {
    const disabled = catSettings.disabled_defaults.includes(key)
      ? catSettings.disabled_defaults.filter(k => k !== key)
      : [...catSettings.disabled_defaults, key];
    saveCatSettings({ ...catSettings, disabled_defaults: disabled });
  };

  const addCustomCategory = () => {
    if (!newCatEn.trim()) return;
    const key = newCatEn.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
    if (activeCategories.some(c => c.key === key)) return;
    const custom = [...catSettings.custom_categories, { key, name_en: newCatEn.trim(), name_bn: newCatBn.trim() || newCatEn.trim() }];
    saveCatSettings({ ...catSettings, custom_categories: custom });
    setNewCatEn(''); setNewCatBn('');
  };

  const removeCustomCategory = (key) => {
    const custom = catSettings.custom_categories.filter(c => c.key !== key);
    saveCatSettings({ ...catSettings, custom_categories: custom });
  };

  const navigateExpenseMonth = (dir) => {
    const [y, m] = expenseMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    const newYear = d.getFullYear();
    const newMonth = d.getMonth() + 1;
    const newExpMonth = `${newYear}-${String(newMonth).padStart(2, '0')}`;
    // Don't navigate into the future
    if (newYear > currentYear || (newYear === currentYear && newMonth > currentMonthNum)) return;
    // Don't navigate beyond 5 years
    if (newYear < currentYear - 4) return;
    setExpenseMonth(newExpMonth);
    if (period === 'month') {
      setSelectedYear(newYear);
      setSelectedMonth(newMonth);
    }
  };

  if (initialLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  // Prepare chart data
  const statusChartData = jobsData?.status_breakdown ? 
    Object.entries(jobsData.status_breakdown).map(([status, count]) => ({
      name: status.replace('_', ' '),
      value: count
    })) : [];

  const categoryChartData = inventoryData?.category_breakdown || [];
  const categoryDefaultOrder = [
    'engine', 'brake', 'electrical', 'transmission', 'accessories',
    'lubricants', 'filters', 'bearings', 'suspension', 'clutch',
    'tires', 'cables', 'body_parts'
  ];

  const categoryMap = new Map(categoryChartData.map(c => [c.category, c]));
  const normalizedCategoryData = [
    ...categoryDefaultOrder.map((cat, idx) => {
      const row = categoryMap.get(cat) || {};
      const labels = CATEGORY_LABELS[cat];
      return {
        category: cat,
        label: labels ? (language === 'en' ? labels.en : labels.bn) : cat,
        stock_value: row.stock_value || 0,
        items: row.items || 0,
        count: row.count || 0,
        _order: idx,
      };
    }),
    ...categoryChartData
      .filter(c => !categoryDefaultOrder.includes(c.category))
      .map((row, idx) => ({
        ...row,
        label: CATEGORY_LABELS[row.category]
          ? (language === 'en' ? CATEGORY_LABELS[row.category].en : CATEGORY_LABELS[row.category].bn)
          : String(row.category || '').replace(/_/g, ' ').replace(/\b\w/g, s => s.toUpperCase()),
        _order: categoryDefaultOrder.length + idx,
      }))
  ];

  const sortedCategoryData = [...normalizedCategoryData].sort((a, b) => {
    if (categorySortMode === 'value-desc') return (b.stock_value || 0) - (a.stock_value || 0);
    if (categorySortMode === 'value-asc') return (a.stock_value || 0) - (b.stock_value || 0);
    if (categorySortMode === 'items-desc') return (b.items || 0) - (a.items || 0);
    if (categorySortMode === 'name') return String(a.label || '').localeCompare(String(b.label || ''));
    return (a._order || 0) - (b._order || 0);
  });

  return (
    <AdminLayout>
      <div className={`space-y-6 transition-opacity duration-200 ${refreshing ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold font-['Barlow_Condensed']">{t('reports_title')}</h1>
            {refreshing && <Spinner size="sm" />}
          </div>
          {activeTab !== 'inventory' && <div className="flex gap-2 items-center flex-wrap">
            {(activeTab === 'payments' ? paymentPeriods : periods).map((p) => (
              <Button
                key={p.value}
                variant={period === p.value ? 'default' : 'outline'}
                onClick={() => setPeriod(p.value)}
                className={period === p.value ? 'bg-blue-800' : ''}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </Button>
            ))}
            {(period === 'month' || period === 'year') && (
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(parseInt(e.target.value))}
                className="h-9 px-3 rounded-md border border-slate-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
            {period === 'month' && (
              <select
                value={selectedMonth}
                onChange={e => setSelectedMonth(parseInt(e.target.value))}
                className="h-9 px-3 rounded-md border border-slate-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {monthOptions.map(m => (
                  <option key={m} value={m}>
                    {language === 'en' ? MONTH_NAMES_EN[m - 1] : MONTH_NAMES_BN[m - 1]}
                  </option>
                ))}
              </select>
            )}
          </div>}
        </div>

        {/* Today's Revenue Breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <DollarSign className="w-6 h-6 opacity-80 mb-1" />
              <p className="text-green-100 text-xs">{language === 'en' ? "Today's Total" : 'আজকের মোট'}</p>
              <p className="text-xl font-bold">{formatCurrency(todayDetail?.today_total || 0, language)}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <Cog className="w-6 h-6 opacity-80 mb-1" />
              <p className="text-blue-100 text-xs">{language === 'en' ? "Today's Services" : 'আজকের সার্ভিস'}</p>
              <p className="text-xl font-bold">{formatCurrency(todayDetail?.today_service_revenue || 0, language)}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <Package className="w-6 h-6 opacity-80 mb-1" />
              <p className="text-purple-100 text-xs">{language === 'en' ? "Today's Parts" : 'আজকের পার্টস'}</p>
              <p className="text-xl font-bold">{formatCurrency(todayDetail?.today_parts_revenue || 0, language)}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-4">
              <TrendingUp className="w-6 h-6 opacity-80 mb-1" />
              <p className="text-emerald-100 text-xs">{language === 'en' ? 'Parts Profit' : 'পার্টস লাভ'}</p>
              <p className="text-xl font-bold">{formatCurrency(todayDetail?.today_parts_profit || 0, language)}</p>
              {todayDetail?.today_parts_revenue > 0 && (
                <p className="text-emerald-200 text-[10px] mt-0.5">
                  {((todayDetail.today_parts_profit / todayDetail.today_parts_revenue) * 100).toFixed(0)}% {language === 'en' ? 'margin' : 'মার্জিন'}
                </p>
              )}
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(`${basePath}/invoices/dues`)}
          >
            <CardContent className="p-4">
              <AlertTriangle className="w-6 h-6 opacity-80 mb-1" />
              <p className="text-orange-100 text-xs">{language === 'en' ? "Today's Due" : 'আজকের বকেয়া'}</p>
              <p className="text-xl font-bold">{formatCurrency(todayDetail?.today_due || 0, language)}</p>
              <p className="text-orange-200 text-[10px] mt-0.5">{language === 'en' ? 'Click to manage →' : 'পরিচালনা →'}</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-slate-600 to-slate-700 text-white">
            <CardContent className="p-4">
              <Wrench className="w-6 h-6 opacity-80 mb-1" />
              <p className="text-slate-300 text-xs">{language === 'en' ? `Period Jobs (${periodLabel})` : `সময়কাল জব (${periodLabel})`}</p>
              <p className="text-xl font-bold">{jobsData?.total_jobs || 0}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="revenue" className="space-y-6" onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="revenue">{t('revenue_report')}</TabsTrigger>
            <TabsTrigger value="profits">{language === 'en' ? 'Profit Report' : 'লাভ-ক্ষতি রিপোর্ট'}{!isPremium && <PremiumBadge size="sm" className="ml-1" />}</TabsTrigger>
            <TabsTrigger value="jobs">{t('jobs_report')}{!isPremium && <PremiumBadge size="sm" className="ml-1" />}</TabsTrigger>
            <TabsTrigger value="payments">{language === 'en' ? 'Payments Report' : 'পেমেন্ট রিপোর্ট'}{!isPremium && <PremiumBadge size="sm" className="ml-1" />}</TabsTrigger>
            <TabsTrigger value="inventory">{t('inventory_report')}</TabsTrigger>
            <TabsTrigger value="marketing">{language === 'en' ? 'Marketing Impact' : 'মার্কেটিং ইমপ্যাক্ট'}{!isPremium && <PremiumBadge size="sm" className="ml-1" />}</TabsTrigger>
          </TabsList>

          {/* Revenue Tab */}
          <TabsContent value="revenue" className="space-y-6">
            {/* Period Revenue Summary Row */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Card>
                <CardContent className="p-5">
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Period Revenue' : 'সময়কাল আয়'}</p>
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(revenueData?.total_revenue || 0, language)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Outstanding Due' : 'বকেয়া'}</p>
                  <p className="text-3xl font-bold text-orange-600">{formatCurrency(revenueData?.total_due || 0, language)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Waived Off' : 'মওকুফ'}</p>
                  <p className="text-3xl font-bold text-orange-500">{formatCurrency(revenueData?.total_waived || 0, language)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Invoices' : 'চালান'}</p>
                  <p className="text-3xl font-bold text-blue-800">{revenueData?.invoice_count || 0}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5">
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Avg Invoice' : 'গড় চালান'}</p>
                  <p className="text-3xl font-bold text-slate-800">
                    {formatCurrency(revenueData?.invoice_count ? revenueData.total_revenue / revenueData.invoice_count : 0, language)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Revenue Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  {period === 'year'
                    ? (language === 'en' ? 'Monthly Revenue Chart' : 'মাসিক আয়ের চার্ট')
                    : (language === 'en' ? 'Daily Revenue Chart' : 'দৈনিক আয়ের চার্ট')
                  }
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dailyChart?.chart_data?.length > 0 ? (
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dailyChart.chart_data}>
                        <defs>
                          <linearGradient id="colorServices" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorParts" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11 }} 
                          tickFormatter={(v) => period === 'year' ? formatMonthShort(v, language) : v.slice(5)}
                        />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip 
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            const data = payload[0]?.payload || {};
                            const displayLabel = period === 'year' ? formatMonthName(label, language) : label;
                            return (
                              <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm min-w-[180px]">
                                <p className="font-bold text-slate-800 mb-2 border-b pb-1">{displayLabel}</p>
                                <div className="space-y-1">
                                  <div className="flex justify-between"><span className="text-blue-600">{language === 'en' ? 'Services' : 'সার্ভিস'}</span><span className="font-medium">{formatCurrency(data.services || 0, language)}</span></div>
                                  <div className="flex justify-between"><span className="text-orange-600">{language === 'en' ? 'Parts' : 'পার্টস'}</span><span className="font-medium">{formatCurrency(data.parts || 0, language)}</span></div>
                                  <div className="flex justify-between border-t pt-1 mt-1"><span className="text-teal-600 font-semibold">{language === 'en' ? 'Total Billed' : 'মোট বিল'}</span><span className="font-bold text-teal-700">{formatCurrency(data.total || 0, language)}</span></div>
                                  {(data.discount || 0) > 0 && <div className="flex justify-between"><span className="text-pink-600">{language === 'en' ? 'Discount' : 'ডিসকাউন্ট'}</span><span className="font-medium">-{formatCurrency(data.discount, language)}</span></div>}
                                  <div className="flex justify-between"><span className="text-green-600">{language === 'en' ? 'Paid' : 'পরিশোধ'}</span><span className="font-medium text-green-700">{formatCurrency(data.paid || 0, language)}</span></div>
                                  {(data.due || 0) > 0 && <div className="flex justify-between"><span className="text-red-600">{language === 'en' ? 'Due' : 'বকেয়া'}</span><span className="font-medium text-red-600">{formatCurrency(data.due, language)}</span></div>}
                                  {(data.waived || 0) > 0 && <div className="flex justify-between"><span className="text-orange-500">{language === 'en' ? 'Waived' : 'মওকুফ'}</span><span className="font-medium text-orange-500">{formatCurrency(data.waived, language)}</span></div>}
                                  <div className="flex justify-between text-slate-500 text-xs pt-1 border-t">
                                    <span>{language === 'en' ? 'Invoices' : 'চালান'}</span><span>{data.invoices || 0}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          }}
                        />
                        <Legend 
                          content={() => (
                            <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 mt-2 text-sm">
                              {[
                                { key: 'total',    color: '#0d9488', label: language === 'en' ? 'Total Billed' : 'মোট বিল',      dash: false },
                                { key: 'paid',     color: '#16a34a', label: language === 'en' ? 'Total Paid'   : 'মোট পরিশোধ',   dash: true  },
                                { key: 'services', color: '#3b82f6', label: language === 'en' ? 'Services'     : 'সার্ভিস',       dash: false },
                                { key: 'parts',    color: '#f97316', label: language === 'en' ? 'Parts'        : 'পার্টস',        dash: false },
                              ].map(({ key, color, label, dash }) => (
                                <span key={key} className="flex items-center gap-1.5">
                                  <svg width="24" height="12">
                                    <line x1="0" y1="6" x2="24" y2="6" stroke={color} strokeWidth="2.5"
                                      strokeDasharray={dash ? '5 3' : 'none'} />
                                    {!dash && <circle cx="12" cy="6" r="3" fill={color} />}
                                  </svg>
                                  <span style={{ color }}>{label}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        />
                        <Line type="monotone" dataKey="total" stroke="#0d9488" strokeWidth={2.5} dot={{ r: 3 }} name="total" />
                        <Area type="monotone" dataKey="services" stroke="#3b82f6" fill="url(#colorServices)" strokeWidth={2} />
                        <Area type="monotone" dataKey="parts" stroke="#f97316" fill="url(#colorParts)" strokeWidth={2} />
                        <Line type="monotone" dataKey="paid" stroke="#16a34a" strokeWidth={2} dot={false} strokeDasharray="5 5" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-slate-500">
                    {language === 'en' ? 'No daily data available' : 'কোন দৈনিক ডেটা নেই'}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== PROFITS TAB ==================== */}
          <TabsContent value="profits" className="space-y-6">
          <PremiumOverlay feature={language === 'en' ? 'Profit Reports' : 'লাভ-ক্ষতি রিপোর্ট'} locked={!isPremium}>

            {/* 3 Big Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Gross Profit */}
              <Card className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="w-7 h-7 opacity-80" />
                    <Badge className="bg-white/20 text-white border-0 text-xs">
                      {profitsData?.gross_margin_pct ?? 0}% {language === 'en' ? 'margin' : 'মার্জিন'}
                    </Badge>
                  </div>
                  <p className="text-emerald-100 text-sm">{language === 'en' ? 'Gross Profit' : 'মোট লাভ'}</p>
                  <p className="text-3xl font-bold">{formatCurrency(profitsData?.gross_profit || 0, language)}</p>
                  <p className="text-emerald-200 text-xs mt-1">
                    {language === 'en' ? 'Before operational costs' : 'অপারেশন খরচের আগে'}
                  </p>
                </CardContent>
              </Card>

              {/* Total Expenses */}
              {(() => {
                const isYearly = period === 'year';
                const displayTotal = isYearly
                  ? (profitsData?.total_expenses || 0)
                  : expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
                return (
                  <Card className="bg-gradient-to-br from-red-500 to-red-700 text-white">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <Banknote className="w-7 h-7 opacity-80" />
                        <Badge className="bg-white/20 text-white border-0 text-xs">
                          {isYearly ? selectedYear : formatMonthName(expenseMonth, language)}
                        </Badge>
                      </div>
                      <p className="text-red-100 text-sm">{language === 'en' ? 'Total Expenses' : 'মোট খরচ'}</p>
                      <p className="text-3xl font-bold">{formatCurrency(displayTotal, language)}</p>
                      <p className="text-red-200 text-xs mt-1">
                        {isYearly
                          ? (language === 'en' ? 'Operational costs this year' : 'এই বছরের অপারেশন খরচ')
                          : (language === 'en' ? 'Operational costs this month' : 'এই মাসের অপারেশন খরচ')
                        }
                      </p>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Net Profit */}
              {(() => {
                const net = profitsData?.net_profit || 0;
                const isPos = net >= 0;
                return (
                  <Card className={`bg-gradient-to-br ${isPos ? 'from-green-600 to-green-800' : 'from-rose-600 to-rose-800'} text-white`}>
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <PiggyBank className="w-7 h-7 opacity-80" />
                        <Badge className="bg-white/20 text-white border-0 text-xs">
                          {profitsData?.net_margin_pct ?? 0}% {language === 'en' ? 'net margin' : 'নেট মার্জিন'}
                        </Badge>
                      </div>
                      <p className={`${isPos ? 'text-green-100' : 'text-rose-100'} text-sm`}>
                        {language === 'en' ? 'Net Profit' : 'নিট লাভ'}
                      </p>
                      <p className="text-3xl font-bold">{formatCurrency(net, language)}</p>
                      <p className={`${isPos ? 'text-green-200' : 'text-rose-200'} text-xs mt-1`}>
                        {isPos
                          ? (language === 'en' ? '✓ Profitable period' : '✓ লাভজনক সময়কাল')
                          : (language === 'en' ? '✗ Loss period' : '✗ ক্ষতির সময়কাল')
                        }
                      </p>
                    </CardContent>
                  </Card>
                );
              })()}
            </div>

            {/* Revenue Breakdown: Services vs Parts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Services card */}
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <p className="font-semibold text-slate-700">{language === 'en' ? 'Services Revenue' : 'সার্ভিস আয়'}</p>
                    {(profitsData?.service_cost || 0) > 0 ? (
                      <Badge className="ml-auto bg-blue-50 text-blue-700 border-blue-200">
                        {profitsData?.service_revenue > 0 ? Math.round(((profitsData.service_revenue - (profitsData.service_cost || 0)) / profitsData.service_revenue) * 100) : 100}% {language === 'en' ? 'margin' : 'মার্জিন'}
                      </Badge>
                    ) : (
                      <Badge className="ml-auto bg-blue-50 text-blue-700 border-blue-200">100% {language === 'en' ? 'margin' : 'মার্জিন'}</Badge>
                    )}
                  </div>
                  {(profitsData?.service_cost || 0) > 0 ? (
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-slate-50 rounded-lg p-2">
                        <p className="text-[10px] text-slate-400 uppercase">{language === 'en' ? 'Revenue' : 'আয়'}</p>
                        <p className="text-sm font-bold text-slate-800">{formatCurrency(profitsData?.service_revenue || 0, language)}</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-2">
                        <p className="text-[10px] text-red-400 uppercase">{language === 'en' ? 'Cost' : 'খরচ'}</p>
                        <p className="text-sm font-bold text-red-600">-{formatCurrency(profitsData?.service_cost || 0, language)}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-2">
                        <p className="text-[10px] text-emerald-500 uppercase">{language === 'en' ? 'Profit' : 'লাভ'}</p>
                        <p className="text-sm font-bold text-emerald-700">{formatCurrency(profitsData?.service_profit || 0, language)}</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-blue-700">{formatCurrency(profitsData?.service_revenue || 0, language)}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {language === 'en' ? 'All service revenue is pure profit' : 'সব সার্ভিস আয় সরাসরি লাভ'}
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Parts card */}
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 rounded-full bg-orange-500" />
                    <p className="font-semibold text-slate-700">{language === 'en' ? 'Parts Revenue & Margin' : 'পার্টস আয় ও মার্জিন'}</p>
                    <Badge className="ml-auto bg-orange-50 text-orange-700 border-orange-200">
                      {profitsData?.parts_margin_pct ?? 0}% {language === 'en' ? 'margin' : 'মার্জিন'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-[10px] text-slate-400 uppercase">
                        {language === 'en' ? 'Selling' : 'বিক্রয়'}
                      </p>
                      <p className="text-sm font-bold text-slate-800">{formatCurrency(profitsData?.parts_revenue || 0, language)}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-2">
                      <p className="text-[10px] text-red-400 uppercase">
                        {language === 'en' ? 'Cost' : 'ক্রয়'}
                      </p>
                      <p className="text-sm font-bold text-red-600">-{formatCurrency(profitsData?.parts_cost || 0, language)}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-2">
                      <p className="text-[10px] text-emerald-500 uppercase">
                        {language === 'en' ? 'Profit' : 'লাভ'}
                      </p>
                      <p className="text-sm font-bold text-emerald-700">{formatCurrency(profitsData?.parts_profit || 0, language)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Profit Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                  {period === 'year'
                    ? (language === 'en' ? 'Monthly Profit Chart' : 'মাসিক লাভের চার্ট')
                    : (language === 'en' ? 'Daily Profit Chart' : 'দৈনিক লাভের চার্ট')
                  }
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profitsData?.daily_chart?.length > 0 ? (
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={profitsData.daily_chart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => period === 'year' ? formatMonthShort(v, language) : v.slice(5)} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0]?.payload || {};
                            const displayLabel = period === 'year' ? formatMonthName(label, language) : label;
                            return (
                              <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm min-w-[190px]">
                                <p className="font-bold text-slate-800 mb-2 border-b pb-1">{displayLabel}</p>
                                <div className="space-y-1">
                                  <div className="flex justify-between"><span className="text-blue-600">{language === 'en' ? 'Services Profit' : 'সার্ভিস লাভ'}</span><span className="font-medium">{formatCurrency(d.services_profit || 0, language)}</span></div>
                                  <div className="flex justify-between"><span className="text-orange-500">{language === 'en' ? 'Parts Margin' : 'পার্টস মার্জিন'}</span><span className="font-medium">{formatCurrency(d.parts_profit || 0, language)}</span></div>
                                  <div className="flex justify-between border-t pt-1 mt-1"><span className="text-emerald-600 font-semibold">{language === 'en' ? 'Gross Profit' : 'মোট লাভ'}</span><span className="font-bold text-emerald-700">{formatCurrency(d.gross_profit || 0, language)}</span></div>
                                  <div className="flex justify-between"><span className="text-green-600">{language === 'en' ? 'Net Profit' : 'নিট লাভ'}</span><span className={`font-bold ${(d.net_profit || 0) >= 0 ? 'text-green-700' : 'text-rose-600'}`}>{formatCurrency(d.net_profit || 0, language)}</span></div>
                                </div>
                              </div>
                            );
                          }}
                        />
                        <Legend
                          content={() => (
                            <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 mt-2 text-sm">
                              {[
                                { color: '#10b981', label: language === 'en' ? 'Gross Profit' : 'মোট লাভ', dash: false },
                                { color: '#16a34a', label: language === 'en' ? 'Net Profit'   : 'নিট লাভ', dash: true  },
                                { color: '#3b82f6', label: language === 'en' ? 'Services'     : 'সার্ভিস', dash: false },
                                { color: '#f97316', label: language === 'en' ? 'Parts Margin' : 'পার্টস মার্জিন', dash: false },
                              ].map(({ color, label, dash }) => (
                                <span key={label} className="flex items-center gap-1.5">
                                  <svg width="24" height="12">
                                    <line x1="0" y1="6" x2="24" y2="6" stroke={color} strokeWidth="2.5" strokeDasharray={dash ? '5 3' : 'none'} />
                                    {!dash && <circle cx="12" cy="6" r="3" fill={color} />}
                                  </svg>
                                  <span style={{ color }}>{label}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        />
                        <Line type="monotone" dataKey="gross_profit" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="net_profit" stroke="#16a34a" strokeWidth={2} dot={false} strokeDasharray="5 5" />
                        <Line type="monotone" dataKey="services_profit" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
                        <Line type="monotone" dataKey="parts_profit" stroke="#f97316" strokeWidth={1.5} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-slate-500">
                    {language === 'en' ? 'No profit data available' : 'কোন লাভের ডেটা নেই'}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Operational Costs Editor */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="flex items-center gap-2">
                    <Banknote className="w-5 h-5 text-red-500" />
                    {language === 'en' ? 'Operational Costs' : 'অপারেশন খরচ'}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {/* Month navigator */}
                    <div className="flex items-center gap-1 bg-slate-100 rounded-lg px-2 py-1">
                      <button onClick={() => navigateExpenseMonth(-1)} className="p-0.5 hover:bg-slate-200 rounded"><ChevronLeft className="w-4 h-4 text-slate-600" /></button>
                      <span className="text-sm font-medium text-slate-700 min-w-[130px] text-center">{formatMonthName(expenseMonth, language)}</span>
                      <button onClick={() => navigateExpenseMonth(1)} className="p-0.5 hover:bg-slate-200 rounded"><ChevronRight className="w-4 h-4 text-slate-600" /></button>
                    </div>
                    <button
                      onClick={() => setShowCatManager(!showCatManager)}
                      className={`p-1.5 rounded-lg transition-colors ${showCatManager ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                      title={language === 'en' ? 'Manage Categories' : 'ক্যাটাগরি পরিচালনা'}
                    >
                      <Settings2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Category Manager Panel */}
                {showCatManager && (
                  <div className="mb-5 p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-4">
                    <p className="text-sm font-semibold text-blue-800">{language === 'en' ? 'Manage Cost Categories' : 'খরচের ক্যাটাগরি পরিচালনা'}</p>

                    {/* Default categories toggle */}
                    <div className="space-y-2">
                      <p className="text-xs text-slate-500 uppercase">{language === 'en' ? 'Default Categories' : 'ডিফল্ট ক্যাটাগরি'}</p>
                      <div className="flex flex-wrap gap-2">
                        {DEFAULT_EXPENSE_CATEGORIES.map(({ key, en, bn }) => {
                          const isDisabled = catSettings.disabled_defaults.includes(key);
                          return (
                            <button
                              key={key}
                              onClick={() => toggleDefault(key)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition-colors ${
                                isDisabled
                                  ? 'bg-slate-100 text-slate-400 border-slate-200'
                                  : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'
                              }`}
                            >
                              {isDisabled ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3 text-blue-500" />}
                              {language === 'en' ? en : bn}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Custom categories */}
                    {catSettings.custom_categories.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs text-slate-500 uppercase">{language === 'en' ? 'Custom Categories' : 'কাস্টম ক্যাটাগরি'}</p>
                        <div className="flex flex-wrap gap-2">
                          {catSettings.custom_categories.map(c => (
                            <span key={c.key} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full bg-white border border-slate-300 text-slate-700">
                              <Tag className="w-3 h-3 text-purple-500" />
                              {language === 'en' ? c.name_en : c.name_bn}
                              <button onClick={() => removeCustomCategory(c.key)} className="ml-1 p-0.5 hover:bg-red-100 rounded-full"><X className="w-3 h-3 text-red-400" /></button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add new */}
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="text-[10px] text-slate-400">{language === 'en' ? 'Name (English)' : 'নাম (ইংরেজি)'}</label>
                        <Input value={newCatEn} onChange={e => setNewCatEn(e.target.value)} placeholder="e.g. Internet" className="h-8 text-sm" />
                      </div>
                      <div className="flex-1">
                        <label className="text-[10px] text-slate-400">{language === 'en' ? 'Name (Bangla)' : 'নাম (বাংলা)'}</label>
                        <Input value={newCatBn} onChange={e => setNewCatBn(e.target.value)} placeholder="যেমন: ইন্টারনেট" className="h-8 text-sm" />
                      </div>
                      <Button size="sm" onClick={addCustomCategory} disabled={!newCatEn.trim()} className="h-8 px-3 bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-3 h-3 mr-1" />{language === 'en' ? 'Add' : 'যোগ'}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeCategories.map(({ key, en, bn, isCustom }) => {
                    const IconComp = isCustom ? Tag : (EXPENSE_ICON_MAP[key] || MoreHorizontal);
                    const savedExpense = expenses.find(e => e.category === key);
                    const savedAmount = savedExpense?.amount || 0;
                    const inputVal = expenseInputs[key] !== undefined ? expenseInputs[key] : savedAmount;
                    const isSaving = expenseSaving[key];
                    const isDirty = parseFloat(inputVal || 0) !== savedAmount;

                    return (
                      <div key={key} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                        <div className="flex items-center gap-2 mb-3">
                          <IconComp className={`w-5 h-5 ${isCustom ? 'text-purple-500' : 'text-slate-500'}`} />
                          <p className="font-medium text-slate-700 text-sm">{language === 'en' ? en : bn}</p>
                          {key === 'salary' && (
                            <button onClick={() => navigate(`${basePath}/employees`)} className="ml-1 text-xs text-blue-500 hover:underline">
                              {language === 'en' ? 'Manage →' : 'ম্যানেজ →'}
                            </button>
                          )}
                          {savedAmount > 0 && (
                            <Badge className="ml-auto bg-red-50 text-red-600 border-red-200 text-xs">
                              {formatCurrency(savedAmount, language)}
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            placeholder="0"
                            value={inputVal}
                            onFocus={e => { if (e.target.value === '0' || e.target.value === 0) setExpenseInputs(prev => ({ ...prev, [key]: '' })); }}
                            onChange={e => setExpenseInputs(prev => ({ ...prev, [key]: e.target.value }))}
                            className="h-8 text-sm"
                          />
                          <Button
                            size="sm"
                            disabled={isSaving || !isDirty}
                            onClick={() => saveExpense(key)}
                            className={`h-8 px-3 text-xs ${isDirty ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-300'}`}
                          >
                            {isSaving ? (
                              <span className="animate-pulse">...</span>
                            ) : (
                              <><Save className="w-3 h-3 mr-1" />{language === 'en' ? 'Save' : 'সংরক্ষণ'}</>
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Expenses Total Footer */}
                <div className="mt-5 flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-200">
                  <p className="font-semibold text-red-800 flex items-center gap-2">
                    <Banknote className="w-4 h-4" />
                    {language === 'en' ? 'Total Operational Costs' : 'মোট অপারেশন খরচ'}
                    <span className="text-xs font-normal text-red-500">({formatMonthName(expenseMonth, language)})</span>
                  </p>
                  <p className="text-2xl font-bold text-red-700">
                    {formatCurrency(expenses.reduce((sum, e) => sum + (e.amount || 0), 0), language)}
                  </p>
                </div>

                {/* Expenses breakdown for the viewed month */}
                {expenses.filter(e => e.amount > 0).length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-slate-400 mb-2 uppercase font-medium">
                      {language === 'en' ? 'Breakdown' : 'বিস্তারিত'}
                    </p>
                    <div className="space-y-1.5">
                      {expenses.filter(e => e.amount > 0).map((e) => {
                        const catDef = activeCategories.find(c => c.key === e.category) || DEFAULT_EXPENSE_CATEGORIES.find(c => c.key === e.category);
                        const BdIcon = catDef?.isCustom ? Tag : (EXPENSE_ICON_MAP[e.category] || MoreHorizontal);
                        return (
                          <div key={e.category} className="flex items-center justify-between text-sm px-2 py-1 rounded-lg hover:bg-slate-50">
                            <span className="flex items-center gap-2 text-slate-600">
                              <BdIcon className="w-4 h-4 text-slate-400" />
                              {language === 'en' ? (catDef?.en || e.category) : (catDef?.bn || e.category)}
                            </span>
                            <span className="font-medium text-red-600">{formatCurrency(e.amount, language)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            </PremiumOverlay>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="space-y-6">
          <PremiumOverlay feature={language === 'en' ? 'Jobs Analytics' : 'জব বিশ্লেষণ'} locked={!isPremium}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'en' ? 'Jobs by Status' : 'স্ট্যাটাস অনুযায়ী জব'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {statusChartData.length > 0 ? (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={statusChartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {statusChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-slate-500">
                      {language === 'en' ? 'No job data' : 'কোন জব ডেটা নেই'}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>{language === 'en' ? 'Top Services' : 'জনপ্রিয় সার্ভিস'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {jobsData?.top_services?.length > 0 ? (
                    <div className="space-y-3">
                      {jobsData.top_services.map((service, index) => (
                        <button
                          key={index}
                          className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-blue-50 transition-colors text-left group"
                          onClick={() => navigate(`${basePath}/jobs?search=${encodeURIComponent(service.name)}`)}
                          title={language === 'en' ? `View jobs with "${service.name}"` : `"${service.name}" সহ জব দেখুন`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-sm font-bold text-blue-800">
                              {index + 1}
                            </span>
                            <span className="font-medium group-hover:text-blue-700">{service.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{service.count} {language === 'en' ? 'jobs' : 'টি জব'}</Badge>
                            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500">{language === 'en' ? 'No service data' : 'কোন সার্ভিস ডেটা নেই'}</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Technician Performance */}
            {jobsData?.technician_performance?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{language === 'en' ? 'Technician Performance' : 'টেকনিশিয়ান পারফরম্যান্স'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {jobsData.technician_performance.map((tech, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-semibold">{tech.name}</p>
                          <p className="text-sm text-slate-500">{tech.jobs} {language === 'en' ? 'jobs assigned' : 'টি জব অ্যাসাইন'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">{tech.completed}</p>
                          <p className="text-xs text-slate-500">{language === 'en' ? 'completed' : 'সম্পন্ন'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            </PremiumOverlay>
          </TabsContent>

          {/* Payments Report Tab */}
          <TabsContent value="payments" className="space-y-6">
          <PremiumOverlay feature={language === 'en' ? 'Payments Report' : 'পেমেন্ট রিপোর্ট'} locked={!isPremium}>
            {paymentsData && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-5">
                      <p className="text-sm text-slate-500">{language === 'en' ? `Total Collected (${periodLabel})` : `মোট সংগ্রহ (${periodLabel})`}</p>
                      <p className="text-3xl font-bold text-green-600">{formatCurrency(paymentsData.total_collected || 0, language)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-5">
                      <p className="text-sm text-slate-500">{language === 'en' ? `Total Payments (${periodLabel})` : `মোট পেমেন্ট (${periodLabel})`}</p>
                      <p className="text-3xl font-bold text-blue-800">{paymentsData.payment_count || 0}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-5">
                      <p className="text-sm text-slate-500">{language === 'en' ? `Methods Used (${periodLabel})` : `পদ্ধতি সংখ্যা (${periodLabel})`}</p>
                      <p className="text-3xl font-bold text-slate-800">{paymentsData.breakdown?.length || 0}</p>
                    </CardContent>
                  </Card>
                </div>
                {paymentsData.breakdown?.length > 0 && (
                  <>
                    <h3 className="text-lg font-semibold">{language === 'en' ? 'Payment Method Breakdown' : 'পেমেন্ট পদ্ধতি বিশ্লেষণ'}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {paymentsData.breakdown.map((item) => {
                        const labels = { cash: language === 'en' ? 'Cash' : 'নগদ', bkash: 'bKash', nagad: 'Nagad', bank: language === 'en' ? 'Bank Transfer' : 'ব্যাংক ট্রান্সফার', card: language === 'en' ? 'Card' : 'কার্ড' };
                        const colors = { cash: 'bg-green-500', bkash: 'bg-pink-500', nagad: 'bg-orange-500', bank: 'bg-blue-500', card: 'bg-purple-500' };
                        const textColors = { cash: 'text-green-600', bkash: 'text-pink-600', nagad: 'text-orange-600', bank: 'text-blue-600', card: 'text-purple-600' };
                        return (
                          <Card key={item.method}>
                            <CardContent className="p-4">
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`w-3 h-3 rounded-full ${colors[item.method] || 'bg-slate-400'}`} />
                                <span className="font-semibold">{labels[item.method] || item.method}</span>
                              </div>
                              <p className={`text-2xl font-bold ${textColors[item.method] || 'text-slate-800'}`}>{formatCurrency(item.amount, language)}</p>
                              <p className="text-xs text-slate-500 mt-1">{item.count} {language === 'en' ? 'payments' : 'টি'} &bull; {item.percentage}%</p>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Animated Pie Chart */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{language === 'en' ? 'Distribution' : 'বন্টন'}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const pieColors = { cash: '#22c55e', bkash: '#ec4899', nagad: '#f97316', bank: '#3b82f6', card: '#a855f7' };
                          const labels = { cash: language === 'en' ? 'Cash' : 'নগদ', bkash: 'bKash', nagad: 'Nagad', bank: language === 'en' ? 'Bank Transfer' : 'ব্যাংক ট্রান্সফার', card: language === 'en' ? 'Card' : 'কার্ড' };
                          const total = paymentsData.breakdown.reduce((s, i) => s + i.amount, 0) || 1;
                          let cumulative = 0;
                          const slices = paymentsData.breakdown.map((item) => {
                            const pct = item.amount / total;
                            const startAngle = cumulative * 360;
                            cumulative += pct;
                            const endAngle = cumulative * 360;
                            const largeArc = (endAngle - startAngle) > 180 ? 1 : 0;
                            const startRad = (startAngle - 90) * Math.PI / 180;
                            const endRad = (endAngle - 90) * Math.PI / 180;
                            const x1 = 50 + 40 * Math.cos(startRad);
                            const y1 = 50 + 40 * Math.sin(startRad);
                            const x2 = 50 + 40 * Math.cos(endRad);
                            const y2 = 50 + 40 * Math.sin(endRad);
                            const d = pct >= 0.9999
                              ? `M 50 10 A 40 40 0 1 1 49.99 10 Z`
                              : `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;
                            return { ...item, d, color: pieColors[item.method] || '#94a3b8', pct };
                          });
                          return (
                            <div className="flex flex-col sm:flex-row items-center gap-6">
                              <div className="relative w-52 h-52 shrink-0">
                                <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-sm">
                                  {slices.map((s, idx) => (
                                    <path
                                      key={s.method}
                                      d={s.d}
                                      fill={s.color}
                                      stroke="white"
                                      strokeWidth="0.5"
                                      className="origin-center transition-transform duration-200 hover:scale-105 cursor-pointer"
                                      style={{
                                        animation: `pieSliceIn 0.8s ease-out ${idx * 120}ms forwards`,
                                        opacity: 0,
                                      }}
                                    >
                                      <title>{labels[s.method] || s.method}: {formatCurrency(s.amount, language)} ({s.percentage}%)</title>
                                    </path>
                                  ))}
                                  <circle cx="50" cy="50" r="22" fill="white" />
                                  <text x="50" y="48" textAnchor="middle" className="text-[7px] font-bold fill-slate-800">{formatCurrency(total, language)}</text>
                                  <text x="50" y="56" textAnchor="middle" className="text-[5px] fill-slate-400">{language === 'en' ? 'Total' : 'মোট'}</text>
                                </svg>
                              </div>
                              <div className="space-y-2.5">
                                {slices.map((s, idx) => (
                                  <div key={s.method} className="flex items-center gap-2.5 text-sm" style={{ animation: `pieLegendIn 0.4s ease-out ${idx * 80 + 400}ms both` }}>
                                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                                    <span className="font-medium">{labels[s.method] || s.method}</span>
                                    <span className="font-bold tabular-nums ml-1">{s.percentage}%</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                        <style>{`
                          @keyframes pieSliceIn {
                            from { opacity: 0; transform: scale(0.7); }
                            to { opacity: 1; transform: scale(1); }
                          }
                          @keyframes pieLegendIn {
                            from { opacity: 0; transform: translateX(-8px); }
                            to { opacity: 1; transform: translateX(0); }
                          }
                        `}</style>
                      </CardContent>
                    </Card>
                  </>
                )}
              </>
            )}
            </PremiumOverlay>
          </TabsContent>

          {/* Inventory Tab */}
          <TabsContent value="inventory" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-5">
                  <Package className="w-8 h-8 text-blue-600 mb-2" />
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Total Parts' : 'মোট পার্টস'}</p>
                  <p className="text-3xl font-bold">{inventoryData?.total_parts || 0}</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <TrendingDown className="w-8 h-8 text-orange-600 mb-2" />
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Stock Buying Value' : 'স্টক ক্রয় মূল্য'}</p>
                  <p className="text-3xl font-bold text-orange-600">
                    {formatCurrency(inventoryData?.total_stock_value || 0, language)}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <TrendingUp className="w-8 h-8 text-blue-600 mb-2" />
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Stock Selling Value' : 'স্টক বিক্রয় মূল্য'}</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {formatCurrency(inventoryData?.total_retail_value || 0, language)}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <TrendingUp className="w-8 h-8 text-green-600 mb-2" />
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Potential Profit' : 'সম্ভাব্য লাভ'}</p>
                  <p className="text-3xl font-bold text-green-600">
                    {formatCurrency(inventoryData?.potential_profit || 0, language)}
                  </p>
                </CardContent>
              </Card>

              <Card className={inventoryData?.low_stock_count > 0 ? 'border-red-300 bg-red-50' : ''}>
                <CardContent className="p-5">
                  <AlertTriangle className={`w-8 h-8 mb-2 ${inventoryData?.low_stock_count > 0 ? 'text-red-600' : 'text-slate-400'}`} />
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Low Stock Items' : 'কম স্টক আইটেম'}</p>
                  <p className={`text-3xl font-bold ${inventoryData?.low_stock_count > 0 ? 'text-red-600' : ''}`}>
                    {inventoryData?.low_stock_count || 0}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <Package className="w-8 h-8 text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">{language === 'en' ? 'Out of Stock' : 'স্টক শেষ'}</p>
                  <p className={`text-3xl font-bold ${(inventoryData?.out_of_stock_count || 0) > 0 ? 'text-red-600' : ''}`}>
                    {inventoryData?.out_of_stock_count || 0}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle>{language === 'en' ? 'Inventory by Category' : 'ক্যাটাগরি অনুযায়ী ইনভেন্টরি'}</CardTitle>
                <select
                  value={categorySortMode}
                  onChange={e => setCategorySortMode(e.target.value)}
                  className="text-xs border rounded-md px-2 py-1 text-slate-600 bg-white focus:outline-none focus:ring-1 focus:ring-orange-300"
                >
                  <option value="default">{language === 'en' ? 'Default Order' : 'ডিফল্ট ক্রম'}</option>
                  <option value="value-desc">{language === 'en' ? 'Value: High → Low' : 'মূল্য: বেশি → কম'}</option>
                  <option value="value-asc">{language === 'en' ? 'Value: Low → High' : 'মূল্য: কম → বেশি'}</option>
                  <option value="items-desc">{language === 'en' ? 'Items: High → Low' : 'আইটেম: বেশি → কম'}</option>
                  <option value="name">{language === 'en' ? 'Name: A → Z' : 'নাম: ক → হ'}</option>
                </select>
              </CardHeader>
              <CardContent>
                {categoryChartData.length > 0 ? (
                  <div className="h-[480px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={sortedCategoryData} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                        <XAxis type="number" tickFormatter={(v) => `৳${v.toLocaleString()}`} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} />
                        <YAxis dataKey="label" type="category" width={110} tick={{ fontSize: 11, fill: '#475569' }} axisLine={false} tickLine={false} />
                        <Tooltip
                          cursor={{ fill: 'rgba(249, 115, 22, 0.06)' }}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #fed7aa', fontSize: '12px' }}
                          formatter={(value, name, entry) => [
                            <span key="v"><b>৳{value?.toLocaleString()}</b> &nbsp;·&nbsp; {entry.payload.items || 0} {language === 'en' ? 'pcs' : 'টি'} &nbsp;·&nbsp; {entry.payload.count || 0} {language === 'en' ? 'SKUs' : 'SKU'}</span>,
                            language === 'en' ? 'Stock Value' : 'স্টক মূল্য'
                          ]}
                        />
                        <Bar dataKey="stock_value" fill="#f97316" radius={[0, 4, 4, 0]} animationDuration={800} animationEasing="ease-out" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-slate-500">
                    {language === 'en' ? 'No inventory data' : 'কোন ইনভেন্টরি ডেটা নেই'}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Low Stock Items */}
            {inventoryData?.low_stock_items?.length > 0 && (
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="text-red-800 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    {language === 'en' ? 'Items Needing Restock' : 'রিস্টক প্রয়োজন'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {inventoryData.low_stock_items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-slate-500">{item.sku}</p>
                        </div>
                        <Badge variant="destructive">{item.stock_quantity} {language === 'en' ? 'left' : 'বাকি'}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ==================== MARKETING IMPACT TAB ==================== */}
          <TabsContent value="marketing" className="space-y-6">
          <PremiumOverlay feature={language === 'en' ? 'Marketing Impact' : 'মার্কেটিং ইমপ্যাক্ট'} locked={!isPremium}>

            {/* ── Hero: SMS ROI vs Normal Customers ── */}
            <Card className="border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-blue-600" />
                  {language === 'en' ? 'Campaign SMS ROI' : 'ক্যাম্পেইন SMS ROI'}
                </CardTitle>
                <p className="text-xs text-slate-500">
                  {language === 'en'
                    ? 'Revenue from customers who received campaign SMS vs those who did not'
                    : 'যেসব কাস্টমারকে ক্যাম্পেইন SMS পাঠানো হয়েছে তাদের আয় বনাম অন্যদের'}
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Bar comparison chart */}
                  <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={[
                        {
                          name: language === 'en' ? 'SMS Customers' : 'SMS কাস্টমার',
                          avg: marketingData?.campaign_roi?.avg_spend_sms_customer || 0,
                          fill: '#2563eb',
                        },
                        {
                          name: language === 'en' ? 'Other Customers' : 'অন্যান্য কাস্টমার',
                          avg: marketingData?.campaign_roi?.avg_spend_other_customer || 0,
                          fill: '#94a3b8',
                        },
                      ]} layout="vertical" margin={{ left: 10, right: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tickFormatter={(v) => `${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                        <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => formatCurrency(v, language)} />
                        <Bar dataKey="avg" radius={[0, 6, 6, 0]} barSize={32}>
                          {[
                            { fill: '#2563eb' },
                            { fill: '#94a3b8' },
                          ].map((entry, i) => (
                            <Cell key={i} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <p className="text-center text-xs text-slate-500 -mt-2">
                      {language === 'en' ? 'Average Spend per Customer' : 'প্রতি কাস্টমার গড় খরচ'}
                    </p>
                  </div>
                  {/* Stats */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-700">{marketingData?.campaign_roi?.sms_sent_to || 0}</p>
                        <p className="text-[10px] text-blue-600">{language === 'en' ? 'SMS Sent To' : 'SMS পাঠানো হয়েছে'}</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-700">{marketingData?.campaign_roi?.customers_returned || 0}</p>
                        <p className="text-[10px] text-green-600">{language === 'en' ? 'Came Back' : 'ফিরে এসেছে'}</p>
                      </div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-slate-600">{language === 'en' ? 'Return Rate' : 'রিটার্ন রেট'}</span>
                        <span className="text-sm font-bold text-green-700">{marketingData?.campaign_roi?.return_rate_pct || 0}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2.5">
                        <div className="bg-green-500 h-2.5 rounded-full transition-all" style={{ width: `${Math.min(100, marketingData?.campaign_roi?.return_rate_pct || 0)}%` }} />
                      </div>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg">
                      <p className="text-xs text-slate-600">{language === 'en' ? 'Revenue from SMS customers' : 'SMS কাস্টমারদের থেকে আয়'}</p>
                      <p className="text-xl font-bold text-emerald-700">{formatCurrency(marketingData?.campaign_roi?.revenue_from_sms_customers || 0, language)}</p>
                      {(marketingData?.campaign_roi?.spend_difference_pct || 0) !== 0 && (
                        <p className={`text-xs mt-1 flex items-center gap-1 ${(marketingData?.campaign_roi?.spend_difference_pct || 0) > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {(marketingData?.campaign_roi?.spend_difference_pct || 0) > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {Math.abs(marketingData?.campaign_roi?.spend_difference_pct || 0)}% {language === 'en' ? 'vs non-SMS customers' : 'অন্যদের তুলনায়'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── Reminder Effectiveness ── */}
            <Card className="border-amber-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  {language === 'en' ? 'Smart Reminder Effectiveness' : 'স্মার্ট রিমাইন্ডার কার্যকারিতা'}
                </CardTitle>
                <p className="text-xs text-slate-500">
                  {language === 'en'
                    ? 'Did customers return after receiving consumable part reminders?'
                    : 'কনজিউমেবল পার্ট রিমাইন্ডার পাওয়ার পর কাস্টমাররা কি ফিরে এসেছে?'}
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Funnel visualization */}
                  <div className="space-y-2">
                    {(() => {
                      const sent = marketingData?.reminder_effectiveness?.total_sent || 0;
                      const reminded = marketingData?.reminder_effectiveness?.unique_customers_reminded || 0;
                      const returned = marketingData?.reminder_effectiveness?.customers_returned || 0;
                      const maxVal = Math.max(sent, 1);
                      return (
                        <>
                          <div className="relative">
                            <div className="h-12 bg-amber-100 rounded-lg flex items-center px-4" style={{ width: '100%' }}>
                              <span className="text-sm font-medium text-amber-800">{sent} {language === 'en' ? 'Reminders Sent' : 'রিমাইন্ডার পাঠানো'}</span>
                            </div>
                          </div>
                          <div className="relative ml-4">
                            <div className="h-12 bg-blue-100 rounded-lg flex items-center px-4" style={{ width: `${Math.max(20, reminded / maxVal * 100)}%` }}>
                              <span className="text-sm font-medium text-blue-800">{reminded} {language === 'en' ? 'Unique Customers' : 'ইউনিক কাস্টমার'}</span>
                            </div>
                          </div>
                          <div className="relative ml-8">
                            <div className="h-12 bg-green-100 rounded-lg flex items-center px-4" style={{ width: `${Math.max(20, returned / maxVal * 100)}%` }}>
                              <span className="text-sm font-medium text-green-800">{returned} {language === 'en' ? 'Came Back' : 'ফিরে এসেছে'}</span>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  {/* Reminder stats */}
                  <div className="space-y-3">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <p className="text-xs text-slate-600">{language === 'en' ? 'Return Rate after Reminder' : 'রিমাইন্ডারের পর রিটার্ন রেট'}</p>
                      <p className="text-2xl font-bold text-green-700">{marketingData?.reminder_effectiveness?.return_rate_pct || 0}%</p>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-lg">
                      <p className="text-xs text-slate-600">{language === 'en' ? 'Revenue from Returned Customers' : 'ফিরে আসা কাস্টমারদের আয়'}</p>
                      <p className="text-xl font-bold text-emerald-700">{formatCurrency(marketingData?.reminder_effectiveness?.revenue_from_returned || 0, language)}</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <p className="text-xs text-slate-600">{language === 'en' ? 'Extra Service Revenue (beyond reminded part)' : 'অতিরিক্ত সার্ভিস আয় (রিমাইন্ড পার্ট ছাড়া)'}</p>
                      <p className="text-xl font-bold text-purple-700">{formatCurrency(marketingData?.reminder_effectiveness?.extra_service_revenue || 0, language)}</p>
                      <p className="text-[10px] text-purple-500 mt-0.5">
                        {language === 'en' ? 'Customers came for the part but also spent on other services!' : 'কাস্টমার পার্টের জন্য এসেছে কিন্তু অন্য সার্ভিসেও খরচ করেছে!'}
                      </p>
                    </div>
                    {(marketingData?.reminder_effectiveness?.auto_cancelled_returned || 0) > 0 && (
                      <div className="p-2 bg-blue-50 rounded-lg text-xs text-blue-700">
                        {marketingData.reminder_effectiveness.auto_cancelled_returned} {language === 'en'
                          ? 'customers returned on their own before reminder was sent'
                          : 'কাস্টমার রিমাইন্ডার পাঠানোর আগেই নিজে থেকে ফিরে এসেছে'}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── Credit Investment vs Revenue Trend ── */}
            {(marketingData?.monthly_trend || []).length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                    {language === 'en' ? 'Are Credits Helping Your Business?' : 'ক্রেডিট কি আপনার ব্যবসায় সাহায্য করছে?'}
                  </CardTitle>
                  <p className="text-xs text-slate-500">
                    {language === 'en'
                      ? 'Monthly trend: credits spent vs revenue from SMS-contacted customers'
                      : 'মাসিক ট্রেন্ড: ক্রেডিট খরচ বনাম SMS কাস্টমারদের আয়'}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={(marketingData?.monthly_trend || []).map(m => ({
                        ...m,
                        monthLabel: formatMonthShort(m.month, language),
                        other_revenue: Math.max(0, m.total_revenue - m.sms_customer_revenue),
                      }))} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={(v) => `${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                        <Tooltip
                          formatter={(v, name) => [formatCurrency(v, language), name]}
                          labelFormatter={(_, payload) => {
                            if (payload?.[0]?.payload?.month) return formatMonthName(payload[0].payload.month, language);
                            return '';
                          }}
                        />
                        <Legend />
                        <Bar
                          dataKey="sms_customer_revenue"
                          name={language === 'en' ? 'SMS Customer Revenue' : 'SMS কাস্টমার আয়'}
                          fill="#2563eb"
                          stackId="rev"
                          radius={[0, 0, 0, 0]}
                        />
                        <Bar
                          dataKey="other_revenue"
                          name={language === 'en' ? 'Other Revenue' : 'অন্যান্য আয়'}
                          fill="#cbd5e1"
                          stackId="rev"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Credits spent line below */}
                  <div className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-2">
                    {(marketingData?.monthly_trend || []).map(m => (
                      <div key={m.month} className="text-center p-2 bg-slate-50 rounded-lg">
                        <p className="text-[10px] text-slate-500">{formatMonthShort(m.month, language)}</p>
                        <p className="text-xs font-bold text-orange-600">{m.credits_spent} {language === 'en' ? 'credits' : 'ক্রেডিট'}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ── Referral Impact ── */}
            <Card className="border-purple-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-purple-600" />
                  {language === 'en' ? 'Referral Impact' : 'রেফারেল ইমপ্যাক্ট'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-700">{marketingData?.referrals?.period_referrals || 0}</p>
                    <p className="text-[10px] text-purple-600">{language === 'en' ? 'New Referrals' : 'নতুন রেফারেল'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-700">{marketingData?.referrals?.all_time_referrals || 0}</p>
                    <p className="text-[10px] text-slate-500">{language === 'en' ? 'All Time' : 'সর্বমোট'}</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-700">{formatCurrency(marketingData?.referrals?.referee_revenue || 0, language)}</p>
                    <p className="text-[10px] text-emerald-600">{language === 'en' ? 'Revenue from Referred' : 'রেফার্ড কাস্টমার আয়'}</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-700">{marketingData?.referrals?.points_awarded || 0}</p>
                    <p className="text-[10px] text-amber-600">{language === 'en' ? 'Points Awarded' : 'পয়েন্ট দেওয়া হয়েছে'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── Per-Campaign Revenue Breakdown ── */}
            {(marketingData?.campaigns || []).length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Send className="w-5 h-5 text-blue-600" />
                    {language === 'en' ? 'Campaign-wise Revenue' : 'ক্যাম্পেইন অনুযায়ী আয়'}
                  </CardTitle>
                  <p className="text-xs text-slate-500">
                    {language === 'en'
                      ? 'How much revenue each campaign generated from contacted customers'
                      : 'প্রতিটি ক্যাম্পেইন থেকে যোগাযোগ করা কাস্টমারদের কত আয় হয়েছে'}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(marketingData?.campaigns || []).map((c) => (
                      <div key={c.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="text-sm font-semibold">{c.name}</p>
                            <p className="text-xs text-slate-500">
                              {c.sent_at ? new Date(c.sent_at).toLocaleDateString() : new Date(c.created_at).toLocaleDateString()}
                              {' '}&middot;{' '}{c.sent_count || 0} {language === 'en' ? 'SMS sent' : 'SMS পাঠানো'}
                            </p>
                          </div>
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            {formatCurrency(c.revenue_generated || 0, language)} {language === 'en' ? 'earned' : 'আয়'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-green-600">
                            {c.customers_returned || 0}/{c.sent_count || 0} {language === 'en' ? 'returned' : 'ফিরেছে'} ({c.return_rate_pct || 0}%)
                          </span>
                          <span className="text-slate-500">
                            {c.credits_used || 0} {language === 'en' ? 'credits used' : 'ক্রেডিট খরচ'}
                          </span>
                          {(c.revenue_generated || 0) > 0 && (c.credits_used || 0) > 0 && (
                            <span className="text-blue-600 font-medium">
                              ROI: {((c.revenue_generated || 0) / (c.credits_used || 1)).toFixed(0)}x
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ── Top Loyalty Customers ── */}
            {(marketingData?.top_loyalty_customers || []).length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Gift className="w-5 h-5 text-emerald-600" />
                    {language === 'en' ? 'Top Loyalty Customers — Revenue Contribution' : 'টপ লয়াল্টি কাস্টমার — আয়ে অবদান'}
                  </CardTitle>
                  <p className="text-xs text-slate-500">
                    {language === 'en'
                      ? 'Your most loyal customers and how much they spent this period'
                      : 'আপনার সবচেয়ে বিশ্বস্ত কাস্টমার এবং এই সময়ে তারা কত খরচ করেছে'}
                  </p>
                </CardHeader>
                <CardContent>
                  {/* Horizontal bar chart of top customers */}
                  {(() => {
                    const topData = (marketingData?.top_loyalty_customers || [])
                      .filter(c => c.period_revenue > 0)
                      .slice(0, 10)
                      .map(c => ({
                        name: c.customer_name?.length > 15 ? c.customer_name.slice(0, 15) + '..' : c.customer_name,
                        revenue: c.period_revenue,
                        points: c.total_points_earned,
                      }));
                    return topData.length > 0 ? (
                      <div className="h-[300px] mb-4">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={topData} layout="vertical" margin={{ left: 5, right: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" tickFormatter={(v) => `${v >= 1000 ? (v/1000).toFixed(0) + 'k' : v}`} />
                            <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(v, name) => [name === 'revenue' ? formatCurrency(v, language) : v, name === 'revenue' ? (language === 'en' ? 'Revenue' : 'আয়') : (language === 'en' ? 'Points' : 'পয়েন্ট')]} />
                            <Bar dataKey="revenue" fill="#059669" radius={[0, 6, 6, 0]} barSize={20} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : null;
                  })()}
                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs text-slate-500">
                          <th className="pb-2 pr-2">#</th>
                          <th className="pb-2 pr-2">{language === 'en' ? 'Customer' : 'কাস্টমার'}</th>
                          <th className="pb-2 pr-2 text-right">{language === 'en' ? 'Points Earned' : 'পয়েন্ট'}</th>
                          <th className="pb-2 pr-2 text-right">{language === 'en' ? 'Redeemed' : 'রিডিম'}</th>
                          <th className="pb-2 pr-2 text-right">{language === 'en' ? 'Period Revenue' : 'আয়'}</th>
                          <th className="pb-2 text-right">{language === 'en' ? 'Invoices' : 'ইনভয়েস'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(marketingData?.top_loyalty_customers || []).map((c, i) => (
                          <tr key={i} className="border-b border-slate-100 last:border-0">
                            <td className="py-2 pr-2 text-slate-400 text-xs">{i + 1}</td>
                            <td className="py-2 pr-2">
                              <p className="font-medium text-sm">{c.customer_name}</p>
                              {c.customer_phone && <p className="text-[10px] text-slate-400">{c.customer_phone}</p>}
                            </td>
                            <td className="py-2 pr-2 text-right text-emerald-600 font-medium">{c.total_points_earned}</td>
                            <td className="py-2 pr-2 text-right text-amber-600">{c.points_redeemed}</td>
                            <td className="py-2 pr-2 text-right font-bold text-slate-800">{formatCurrency(c.period_revenue, language)}</td>
                            <td className="py-2 text-right text-slate-600">{c.period_invoices}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ── Summary Footer ── */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-slate-100 rounded-lg text-center">
                <p className="text-xs text-slate-500">{language === 'en' ? 'Total Revenue' : 'মোট আয়'}</p>
                <p className="text-lg font-bold text-slate-800">{formatCurrency(marketingData?.total_revenue || 0, language)}</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-xs text-blue-600">{language === 'en' ? 'Credits Used' : 'ক্রেডিট ব্যবহার'}</p>
                <p className="text-lg font-bold text-blue-700">{marketingData?.total_credits_used || 0}</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                <p className="text-xs text-emerald-600">{language === 'en' ? 'Credits Balance' : 'ক্রেডিট ব্যালেন্স'}</p>
                <p className="text-lg font-bold text-emerald-700">{marketingData?.credits_balance || 0}</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg text-center">
                <p className="text-xs text-orange-600">{language === 'en' ? 'Credit ROI' : 'ক্রেডিট ROI'}</p>
                <p className="text-lg font-bold text-orange-700">
                  {(marketingData?.total_credits_used || 0) > 0
                    ? `${(((marketingData?.campaign_roi?.revenue_from_sms_customers || 0) + (marketingData?.reminder_effectiveness?.revenue_from_returned || 0)) / (marketingData?.total_credits_used || 1)).toFixed(0)}x`
                    : '-'}
                </p>
                <p className="text-[10px] text-orange-500">{language === 'en' ? 'revenue per credit' : 'প্রতি ক্রেডিটে আয়'}</p>
              </div>
            </div>

          </PremiumOverlay>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
